// src/components/FloatingContact.js
import React, { useState } from 'react';
import axios from 'axios';
import '../styles/FloatingContact.css';

// Create a context for the FloatingContact functionality
export const FloatingContactContext = React.createContext();

export const FloatingContactProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleToggle = () => {
    setIsOpen(!isOpen);

    // Reset state if closing
    if (isOpen) {
      setSubmitting(false);
      setSubmitted(false);
      setError('');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    }
  };

  const handleOpenContact = () => {
    setIsOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await axios.post('/api/contact', formData);

      if (response.data.success) {
        setSubmitted(true);
        // Reset form after 3 seconds
        setTimeout(() => {
          setIsOpen(false);
          setTimeout(() => {
            setSubmitted(false);
            setFormData({
              name: '',
              email: '',
              phone: '',
              subject: '',
              message: ''
            });
          }, 300);
        }, 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FloatingContactContext.Provider value={{ handleOpenContact }}>
      {children}
      <div className={`floating-contact ${isOpen ? 'open' : ''}`}>
        <button 
          className="contact-toggle" 
          onClick={handleToggle}
          aria-label={isOpen ? "Close contact form" : "Open contact form"}
        >
          {isOpen ? (
            <i className="fas fa-times"></i>
          ) : (
            <>
              <i className="fas fa-comment-alt"></i>
              <span>Contact Us</span>
            </>
          )}
        </button>

        <div className="contact-panel" style={!isOpen ? { display: 'none' } : undefined}>
          <div className="contact-header">
            <h2>Get In Touch</h2>
            <p>Have questions? We're here to help!</p>
          </div>

          {submitted ? (
            <div className="success-message">
              <i className="fas fa-check-circle"></i>
              <h3>Message Sent!</h3>
              <p>Thank you for contacting us. We'll get back to you within 1-2 business days.</p>
            </div>
          ) : (
            <div className="contact-content">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="contact-name">Name *</label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Your name"
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact-email">Email *</label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your@email.com"
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact-phone">Phone</label>
                  <input
                    type="tel"
                    id="contact-phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(123) 456-7890"
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact-subject">Subject</label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="What can we help with?"
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact-message">Message *</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    placeholder="Tell us about your needs..."
                    disabled={submitting}
                  ></textarea>
                </div>

                {error && (
                  <div className="error-message">
                    <i className="fas fa-exclamation-circle"></i>
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Sending...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i> Send Message
                    </>
                  )}
                </button>
              </form>

              <div className="contact-divider">
                <span>or reach us directly</span>
              </div>

              <div className="contact-methods">
                <div className="contact-method">
                  <i className="fas fa-phone"></i>
                  <span>(681) 881-1195</span>
                </div>
                <div className="contact-method">
                  <i className="fas fa-envelope"></i>
                  <span>legate@legatebusiness.com</span>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </FloatingContactContext.Provider>
  );
};

const FloatingContact = () => {
  // This is just a placeholder component that will be used in places where we need
  // to include the FloatingContact component but don't want to render anything
  return null;
};

export default FloatingContact;
